import React, { useContext, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import tw from "twin.macro"
import {
  setActiveDisclaimer,
  setActiveDisclaimerPopover,
  advanceDisclaimerQueue,
} from "../../../contexts/Disclaimers/actions"
import { ProgressRing, X, DisclaimerInfo } from "../../atoms/Icon"
import { DisclaimerPopoverProps } from "./DisclaimerPopover.d"
import useComponentId from "../../../hooks/useIdGenerator"
import { LanguageContext } from "../../../contexts/Language"
import { createPortal } from "react-dom"
import {
  DisclaimersContext,
  setIsAnyDisclaimerPopoverOpen,
  isDisclaimerModalOpen,
  setModalOpenedFromQueue,
} from "../../../contexts/Disclaimers/context"
import { Disclaimer } from "../../../contexts/Disclaimers/disclaimers.d"

/**
 * Disclaimer Popover
 *
 * @component Disclaimer Popover
 * @summary - A popover for a disclaimer and a button that triggers opening the popover.
 *
 * @param {function} onClick - onClick function for View All Disclaimers button inside popover.
 * @param {string} code - Disclaimer code
 * @returns <span></span> - containing disclaimer popover trigger and disclaimer popover
 */

const DisclaimerPopover: React.FC<DisclaimerPopoverProps> = ({
  onClick,
  code,
  onViewAllDisclosures,
  ...remainingProps
}) => {
  const uuid = useComponentId()
  const { language, _ } = useContext(LanguageContext)
  const [
    {
      disclaimers,
      openOnLoad,
      activeDisclaimerPopoverId,
      disclaimerQueue,
      currentDisclaimerIndex,
    },
    dispatch,
  ] = useContext(DisclaimersContext)
  const filteredResult = disclaimers?.filter(
    disclaimer => disclaimer.code === code,
  )
  const filteredDisclaimers: Disclaimer | undefined =
    filteredResult?.length > 0 ? filteredResult[0] : undefined

  const { width, height } = remainingProps
  const AUTO_DISMISS_TIME = 3000

  const buttonRef = useRef<HTMLButtonElement>(null)
  const popOverRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)

  const [popover, setPopover] = useState<boolean>(false)
  const [open, setOpen] = useState<boolean>(false)
  const [isOpenOnLoad, setIsOpenOnLoad] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [wasClosedForModal, setWasClosedForModal] = useState<boolean>(false)
  const { ref, inView, entry } = useInView({
    threshold: 1,
    triggerOnce: true,
  })

  // Custom hook to check if any disclaimer popover is open
  useEffect(() => {
    if (open) {
      setIsAnyDisclaimerPopoverOpen(true)
    } else {
      const otherPopovers = document.querySelectorAll(
        '[data-disclaimer-popover="true"]',
      )
      setIsAnyDisclaimerPopoverOpen(otherPopovers.length > 0)
    }
  }, [open])

  // Auto-open on load if flagged and this is the current disclaimer in the queue
  useEffect(() => {
    if (!openOnLoad || !inView || !disclaimerQueue.length) return

    // Only open if this disclaimer is the current one in the queue
    const currentDisclaimerCode = disclaimerQueue[currentDisclaimerIndex]
    if (inView && currentDisclaimerCode === code) {
      setIsOpenOnLoad(true)
      openPopover()
    }
  }, [openOnLoad, inView, disclaimerQueue, currentDisclaimerIndex, code])

  useEffect(() => {
    if (!isOpenOnLoad) return

    const timer = setInterval(() => {
      // Pause progress when disclaimer modal is open
      if (isDisclaimerModalOpen) {
        return
      }

      setProgress(progress => {
        if (progress + 1 <= 100) {
          return progress + 1
        }
        return progress
      })
    }, AUTO_DISMISS_TIME / 100)
    return () => clearInterval(timer)
  }, [isOpenOnLoad])

  useEffect(() => {
    const isNotSelected = isOpenOnLoad
      ? false
      : activeDisclaimerPopoverId != uuid
    if (progress === 100 || isNotSelected) {
      closePopover()
    }
  }, [progress, activeDisclaimerPopoverId, isOpenOnLoad])

  // Reset modal flag when this disclaimer closes
  useEffect(() => {
    if (!open && wasClosedForModal) {
      setWasClosedForModal(false)
    }
  }, [open, wasClosedForModal])

  const closePopover = (e?: React.MouseEvent) => {
    // Completely stop all event propagation so navmenu doesnt close when popover closes
    if (e) {
      e.stopPropagation()
      e.preventDefault()
      // Additional prevention for all event phases
      e.nativeEvent?.stopImmediatePropagation?.()
    }

    if (!open) return
    setOpen(false)
    if (activeDisclaimerPopoverId) dispatch(setActiveDisclaimerPopover(0))
    dispatch(setActiveDisclaimer(""))
    isOpenOnLoad && setIsOpenOnLoad(false)

    setTimeout(() => {
      //Once the popover DOM is gone, clear the flag
      setIsAnyDisclaimerPopoverOpen(false)

      setPopover(false)
      setIsOpenOnLoad(false)
      setProgress(0)

      // If this was an auto-opened disclaimer and there are more in the queue, advance to the next one
      // But only if the disclaimer modal is not open and this wasn't closed for the modal
      if (
        isOpenOnLoad &&
        disclaimerQueue.length > 0 &&
        currentDisclaimerIndex < disclaimerQueue.length - 1 &&
        !isDisclaimerModalOpen &&
        !wasClosedForModal
      ) {
        dispatch(advanceDisclaimerQueue())
      }
    }, 200)
  }

  const openPopover = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }

    if (open) return
    if (!isOpenOnLoad) {
      dispatch(setActiveDisclaimer(code))
      dispatch(setActiveDisclaimerPopover(uuid))
    }
    setPopover(true)
    setTimeout(() => {
      setOpen(true)
    }, 20)
  }

  // Handle backdrop clicks more specifically
  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent?.stopImmediatePropagation?.()

    // Only close if clicking directly on the backdrop, not on child elements
    if (e.target === e.currentTarget) {
      closePopover(e)
    }
  }

  // Handle all clicks within the popover to prevent bubbling
  const handlePopoverClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent?.stopImmediatePropagation?.()
  }

  // Enhanced close button handler
  const handleCloseButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent?.stopImmediatePropagation?.()
    closePopover(e)
  }

  // Enhanced view all disclaimers handler
  const handleViewAllClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent?.stopImmediatePropagation?.()

    // Mark that this disclaimer was closed to show the modal
    if (isOpenOnLoad) {
      setWasClosedForModal(true)
      // Also mark globally that modal was opened from queue
      setModalOpenedFromQueue(true)
    }

    closePopover(e)
    onClick(e)
  }

  if (!filteredDisclaimers) return null

  let disclaimerText
  if (language === "es") {
    disclaimerText = filteredDisclaimers?.disclaimer
      ? filteredDisclaimers?.disclaimer
      : filteredDisclaimers?.disclaimer_es
  } else {
    disclaimerText = filteredDisclaimers?.disclaimer
      ? filteredDisclaimers?.disclaimer
      : filteredDisclaimers?.disclaimer_en
  }
  let extraDisclaimer = filteredDisclaimers?.extraDisclaimer

  return (
    <>
      <span css={[tw`inline`]} ref={ref}>
        <button
          onMouseDown={openPopover}
          ref={buttonRef}
          css={[
            tw`focus-visible:(outline-[1px dotted currentColor])`,
            tw`focus-visible:(outline-[1px dotted currentColor]) hover:opacity-70 transition-opacity`,
            "outline-offset: 2px !important;",
          ]}
          aria-label="Open disclosure information"
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <DisclaimerInfo css={[tw`h-3`]} />
        </button>
      </span>

      {popover &&
        createPortal(
          <div
            css={[
              tw`fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50`,
              tw`transition-opacity duration-150`,
              open ? tw`opacity-100` : tw`opacity-0`,
            ]}
            onMouseDown={handleBackdropClick}
            ref={backdropRef}
            data-disclaimer-popover="true" // data attribute for tracking
          >
            <div
              css={[
                tw`relative whitespace-normal p-6 max-w-[400px] w-full text-gray-900 bg-gray-100 border-gray-200 border shadow-lg rounded-lg transform transition-all duration-150`,
                tw`max-h-[80vh] overflow-y-auto`,
                open ? tw`opacity-100 scale-100` : tw`opacity-0 scale-95`,
                height && `max-height: min(${height}px, 80vh);`,
              ]}
              ref={popOverRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`disclaimer-${code}-title`}
              onClick={handlePopoverClick}
            >
              {isOpenOnLoad && (
                <ProgressRing
                  color="gray-600"
                  progress={progress}
                  css={[tw`h-4 w-4 top-2 right-2 absolute`]}
                />
              )}
              <button
                css={[
                  tw`absolute right-2 top-2 border-2 border-transparent rounded-full`,
                ]}
                onClick={handleCloseButtonClick}
                aria-label="Close disclaimer"
              >
                <X
                  color="gray-600"
                  css={[tw`h-3`, isOpenOnLoad && tw`transform scale-50`]}
                />
              </button>
              <div
                css={[
                  tw`font-book text-xs max-h-80 overflow-y-auto`,
                  height && `max-height: ${height - 40}px;`,
                ]}
              >
                <p
                  id={`disclaimer-${code}-title`}
                  className="disclaimer"
                  css={[tw`italic mb-4 text-left tracking-normal normal-case`]}
                  dangerouslySetInnerHTML={{
                    __html: disclaimerText,
                  }}
                />
                {extraDisclaimer && (
                  <p
                    className="disclaimer"
                    css={[
                      tw`italic mb-4 text-left tracking-normal normal-case`,
                    ]}
                    dangerouslySetInnerHTML={{
                      __html: extraDisclaimer,
                    }}
                  />
                )}
                <div css={[tw`flex justify-between`]}>
                  <button css={[tw`underline`]} onClick={handleViewAllClick}>
                    {_("View All Disclosures")}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

export default React.memo(
  DisclaimerPopover,
  (prev, next) => prev.code === next.code,
)
